import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { safeParseJson } from '../lib/storage-utils';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, Edit2, Trash2, Users, Lock, ShoppingCart, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';

interface Relationship {
  id: string;
  name: string;
  type: string;
  length: string;
  livingSituation: string;
  communicationBaseline: number;
  createdAt: string;
  isActive: boolean;
}

const TIER_LIMITS = {
  free: 2,
  premium: 3,
  pro: 5,
};

export default function RelationshipManagement() {
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [purchasingSlot, setPurchasingSlot] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    length: '',
    livingSituation: '',
    communicationBaseline: 50,
  });
  const auth = useAuth();
  const navigate = useNavigate();
  
  // Defensive: handle potential undefined auth during hot reload
  const user = auth?.user || null;
  const accessToken = auth?.accessToken || null;

  // Get subscription tier and additional slots
  const subscriptionTier = localStorage.getItem('tension_subscription') || 'free';
  const additionalSlots = parseInt(localStorage.getItem('tension_additional_slots') || '0');
  const baseLimit = TIER_LIMITS[subscriptionTier as keyof typeof TIER_LIMITS] || 2;
  const maxRelationships = baseLimit + additionalSlots;

  // Load relationships (safe parse to avoid crash on corrupted localStorage)
  useEffect(() => {
    const parsed = safeParseJson<Relationship[]>(localStorage.getItem('tension_relationships'), []);
    setRelationships(Array.isArray(parsed) ? parsed : []);
  }, []);

  // Save relationships
  const saveRelationships = (updated: Relationship[]) => {
    localStorage.setItem('tension_relationships', JSON.stringify(updated));
    setRelationships(updated);
  };

  const handleAddRelationship = () => {
    if (relationships.length >= maxRelationships) {
      toast.error(`You've reached the limit for your ${subscriptionTier} plan (${maxRelationships} relationships)`);
      return;
    }

    if (!formData.name || !formData.type || !formData.length || !formData.livingSituation) {
      toast.error('Please fill in all fields');
      return;
    }

    const newRelationship: Relationship = {
      id: `rel_${Date.now()}`,
      name: formData.name,
      type: formData.type,
      length: formData.length,
      livingSituation: formData.livingSituation,
      communicationBaseline: formData.communicationBaseline,
      createdAt: new Date().toISOString(),
      isActive: relationships.length === 0, // First relationship is active by default
    };

    saveRelationships([...relationships, newRelationship]);
    
    // If this is the first relationship, set it as active
    if (relationships.length === 0) {
      localStorage.setItem('tension_active_relationship', newRelationship.id);
    }

    toast.success(`Added ${formData.name}`);
    resetForm();
  };

  const handleUpdateRelationship = () => {
    if (!editingId) return;

    const updated = relationships.map((rel) =>
      rel.id === editingId
        ? { ...rel, ...formData }
        : rel
    );

    saveRelationships(updated);
    toast.success('Relationship updated');
    resetForm();
  };

  const handleDeleteRelationship = (id: string) => {
    if (!confirm('Are you sure you want to delete this relationship? All associated data will be removed.')) {
      return;
    }

    const updated = relationships.filter((rel) => rel.id !== id);
    
    // If deleting active relationship, set first remaining as active
    const activeId = localStorage.getItem('tension_active_relationship');
    if (activeId === id && updated.length > 0) {
      const newActive = updated[0];
      newActive.isActive = true;
      localStorage.setItem('tension_active_relationship', newActive.id);
    }

    saveRelationships(updated);

    const checkins = safeParseJson<Array<{ relationshipId?: string }>>(
      localStorage.getItem('tension_checkins'),
      [],
    );
    const filteredCheckins = Array.isArray(checkins) ? checkins.filter((c) => c.relationshipId !== id) : [];
    localStorage.setItem('tension_checkins', JSON.stringify(filteredCheckins));

    toast.success('Relationship deleted');
  };

  const setActiveRelationship = (id: string) => {
    const updated = relationships.map((rel) => ({
      ...rel,
      isActive: rel.id === id,
    }));
    saveRelationships(updated);
    localStorage.setItem('tension_active_relationship', id);
    toast.success('Active relationship switched');
  };

  const validTypes = ['romantic', 'dating', 'marriage', 'partnership', 'casual'];
  const validLengths = ['0-3', '3-6', '6-12', '1-2', '2-5', '5+'];
  const validLiving = ['together', 'separate', 'long-distance'];

  const startEdit = (rel: Relationship) => {
    setEditingId(rel.id);
    setFormData({
      name: rel.name ?? '',
      type: validTypes.includes(rel.type) ? rel.type : 'dating',
      length: validLengths.includes(rel.length) ? rel.length : '0-3',
      livingSituation: validLiving.includes(rel.livingSituation) ? rel.livingSituation : 'separate',
      communicationBaseline: typeof rel.communicationBaseline === 'number' ? rel.communicationBaseline : 50,
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      length: '',
      livingSituation: '',
      communicationBaseline: 50,
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const activeRelationship = relationships.find((r) => r.isActive);
  const canAddMore = relationships.length < maxRelationships;

  const purchaseAdditionalSlot = async () => {
    if (!user || !accessToken) {
      toast.error('Please sign in to purchase additional slots');
      navigate('/auth');
      return;
    }

    setPurchasingSlot(true);
    console.log('💳 PURCHASING ADDITIONAL RELATIONSHIP SLOT');
    console.log('👤 User:', user.id);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/purchase-relationship-slot`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-User-Token': accessToken,
          },
        }
      );

      console.log('📡 Response status:', response.status);
      const data = await response.json();
      console.log('📦 Response data:', data);

      if (!response.ok) {
        console.error('❌ Server error:', data);
        toast.error(data.error || 'Purchase failed. Please try again.');
        setPurchasingSlot(false);
        return;
      }

      if (data.url) {
        console.log('✅ Redirecting to Stripe...');
        toast.success('Opening checkout in new tab...');
        
        // Open Stripe in a new tab
        const stripeWindow = window.open(data.url, '_blank');
        
        if (!stripeWindow) {
          toast.error('Please allow popups to complete checkout');
        } else {
          toast.info('Stripe checkout opening in new tab...', { duration: 2000 });
          
          setTimeout(() => {
            toast.success(
              'After payment: 1) Close the Stripe tab, 2) Go to Settings, 3) Click \"Sync\" to activate your additional slot',
              { duration: 10000 }
            );
          }, 2500);
        }
        
        setPurchasingSlot(false);
      } else {
        console.error('❌ No checkout URL in response');
        toast.error('No checkout URL received');
        setPurchasingSlot(false);
      }
    } catch (error) {
      console.error('❌ Purchase error:', error);
      toast.error('Network error. Please check your connection.');
      setPurchasingSlot(false);
    }
  };

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/settings')}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-neutral-600" />
            </button>
            <div>
              <h1 className="text-xl font-light">Relationships</h1>
              <p className="text-xs text-neutral-500">
                {relationships.length}/{maxRelationships} relationships
              </p>
            </div>
          </div>
          {canAddMore && !showAddForm && (
            <Button
              onClick={() => setShowAddForm(true)}
              size="sm"
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Add
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-6 space-y-6">
        {/* Tier Info */}
        <Card className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-violet-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-neutral-900 text-sm">
                {subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)} Plan
              </h3>
              <p className="text-xs text-neutral-600 mt-1">
                Track up to {maxRelationships} relationships. 
                {subscriptionTier === 'free' && ' Upgrade to Premium for 3 relationships.'}
                {subscriptionTier === 'premium' && ' Upgrade to Pro for 5 relationships.'}
              </p>
            </div>
          </div>
        </Card>

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-light">
                {editingId ? 'Edit Relationship' : 'Add Relationship'}
              </h2>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                Cancel
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Partner's Name (or Nickname)</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Alex, Sarah, J..."
                  maxLength={20}
                />
              </div>

              <div>
                <Label>Relationship Type</Label>
                <Select value={formData.type} onValueChange={(val) => setFormData({ ...formData, type: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="romantic">Romantic</SelectItem>
                    <SelectItem value="dating">Early-stage</SelectItem>
                    <SelectItem value="marriage">Marriage</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Relationship Length</Label>
                <Select value={formData.length} onValueChange={(val) => setFormData({ ...formData, length: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-3">0-3 months</SelectItem>
                    <SelectItem value="3-6">3-6 months</SelectItem>
                    <SelectItem value="6-12">6-12 months</SelectItem>
                    <SelectItem value="1-2">1-2 years</SelectItem>
                    <SelectItem value="2-5">2-5 years</SelectItem>
                    <SelectItem value="5+">5+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Living Situation</Label>
                <Select
                  value={formData.livingSituation}
                  onValueChange={(val) => setFormData({ ...formData, livingSituation: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select situation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="together">Living together</SelectItem>
                    <SelectItem value="separate">Living separately</SelectItem>
                    <SelectItem value="long-distance">Long distance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={editingId ? handleUpdateRelationship : handleAddRelationship}
                className="w-full"
              >
                {editingId ? 'Save Changes' : 'Add Relationship'}
              </Button>
            </div>
          </Card>
        )}

        {/* Relationship List */}
        {relationships.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-light text-neutral-600 mb-2">No relationships yet</h3>
            <p className="text-sm text-neutral-500 mb-6">
              Add your first relationship to start tracking tension
            </p>
            <Button onClick={() => setShowAddForm(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Add Relationship
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {relationships.map((rel) => (
              <Card
                key={rel.id}
                className={`p-4 transition-all ${
                  rel.isActive
                    ? 'border-violet-300 bg-violet-50/50 shadow-sm'
                    : 'hover:border-neutral-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-neutral-900">{rel.name}</h3>
                      {rel.isActive && (
                        <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-600">
                      {typeof rel.type === 'string' && rel.type
                        ? rel.type.charAt(0).toUpperCase() + rel.type.slice(1)
                        : 'Relationship'}{' '}
                      • {typeof rel.length === 'string' ? rel.length : '—'}{' '}
                      •{' '}
                      {rel.livingSituation === 'together'
                        ? 'Living together'
                        : rel.livingSituation === 'separate'
                          ? 'Living separately'
                          : rel.livingSituation === 'long-distance'
                            ? 'Long distance'
                            : typeof rel.livingSituation === 'string'
                              ? rel.livingSituation
                              : '—'}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {!rel.isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setActiveRelationship(rel.id)}
                      >
                        Set Active
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(rel)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteRelationship(rel.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Upgrade CTA if at limit */}
        {!canAddMore && (
          <Card className="p-6 bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-violet-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-neutral-900 mb-1">Relationship Limit Reached</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  You've used all {maxRelationships} relationship slots on your {subscriptionTier} plan.
                  {subscriptionTier === 'free' && ' Upgrade to Premium for 3 relationships or Pro for 5.'}
                  {subscriptionTier === 'premium' && ' Upgrade to Pro for 5 relationships.'}
                  {(subscriptionTier === 'premium' || subscriptionTier === 'pro') && ' Or purchase additional slots for $4.99 each.'}
                </p>
                <div className="flex gap-3">
                  <Button size="sm" onClick={() => navigate('/pricing')}>
                    View Plans
                  </Button>
                  <Button size="sm" variant="outline" onClick={purchaseAdditionalSlot} disabled={purchasingSlot}>
                    {purchasingSlot ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
                    <span className="ml-2">Add Slot • $4.99</span>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}